/**
 * One stack for every overlay (modal, drawer, popover, confirm dialog).
 *
 * Overlays nest — a confirm dialog opens from inside a drawer, a menu from
 * inside a modal — and each used to own its own Escape and outside-click
 * handling, so one keypress closed every layer at once and a click inside a
 * popover read as a backdrop click on the drawer underneath. The stack makes
 * "who is on top" a single fact: only the top layer hears Escape, and a
 * layer asks `isTop()` before acting on an outside click.
 *
 * Escape is handled on `window` in the bubble phase, so a component that
 * handles Escape itself (and stops propagation or calls `preventDefault`)
 * wins — the stack is the fallback, not an override.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export type LayerKind = "modal" | "drawer" | "popover" | "dialog";

interface Layer {
  id: number;
  kind: LayerKind;
  onEscape: () => void;
}

const stack: Layer[] = [];
const listeners = new Set<() => void>();
let nextId = 1;

function emit() {
  for (const fn of listeners) fn();
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== "Escape" || e.defaultPrevented || e.isComposing) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  e.preventDefault();
  top.onEscape();
}

function ensureListener() {
  if (typeof window === "undefined") return;
  window.removeEventListener("keydown", onKeyDown);
  window.addEventListener("keydown", onKeyDown);
}

/** True while any overlay (optionally of the given kinds) is open. */
export function hasOpenLayer(kinds?: LayerKind[]): boolean {
  return kinds ? stack.some((l) => kinds.includes(l.kind)) : stack.length > 0;
}

/** Subscribe to stack changes (for hooks that must disable themselves). */
export function subscribeLayers(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Register an overlay while `open`. Returns `isTop()` — call it inside event
 * handlers (never during render) before acting on outside clicks or focus.
 * `onEscape` may change every render; the latest one runs.
 */
export function useLayer(open: boolean, kind: LayerKind, onEscape: () => void): () => boolean {
  const escapeRef = useRef(onEscape);
  const idRef = useRef(0);

  useEffect(() => {
    escapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!open) return;
    const id = nextId++;
    idRef.current = id;
    stack.push({ id, kind, onEscape: () => escapeRef.current() });
    ensureListener();
    emit();
    return () => {
      const i = stack.findIndex((l) => l.id === id);
      if (i >= 0) stack.splice(i, 1);
      if (idRef.current === id) idRef.current = 0;
      emit();
    };
  }, [open, kind]);

  return useCallback(
    () => idRef.current !== 0 && stack[stack.length - 1]?.id === idRef.current,
    [],
  );
}

/** Re-render when the layer stack changes; returns whether any layer is open. */
export function useAnyLayerOpen(kinds?: LayerKind[]): boolean {
  const [open, setOpen] = useState(() => hasOpenLayer(kinds));
  const kindsKey = kinds?.join(",") ?? "";
  useEffect(() => {
    const list = kindsKey ? (kindsKey.split(",") as LayerKind[]) : undefined;
    const update = () => setOpen(hasOpenLayer(list));
    update();
    return subscribeLayers(update);
  }, [kindsKey]);
  return open;
}

/*
 * Outside-click handshake. A click outside an open popover closes the popover
 * on pointerdown; the same gesture must not then also count as a backdrop
 * click on the dialog underneath. The popover records the gesture's target,
 * and `useDialog` ignores a backdrop press that started on it.
 */
let lastDismiss: { target: EventTarget | null; at: number } | null = null;

export function markOutsideDismiss(target: EventTarget | null): void {
  lastDismiss = { target, at: performance.now() };
}

export function isDismissGesture(target: EventTarget | null): boolean {
  return !!lastDismiss && lastDismiss.target === target && performance.now() - lastDismiss.at < 1000;
}
